import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, Globe, MapPin, User2, X } from "lucide-react";
import { useEffect, useState } from "react";

import { FileOutlineIcon, InfoCircle } from "../../../assets/icons/interface/interface";
import { Badge } from "../../../components/Badge/Badge";
import DetailedTooltip from "../../../components/DetailedTooltip/DetailedTooltip";
import Icon from "../../../components/Icon";
import { useAuth } from "../../../context/Auth/AuthContext";
import { cn } from "../../../handlers/cn.utils";
import { sections } from "../../../handlers/enums";
import { capitalizeFirstLetterOfWords } from "../../../handlers/utilityFunctions";
import { useDSPInfo } from "../../../services/create-resume/queries";
import { Button } from "../../JobsPage/components/Button";
import Loader from "../../StaticPages/HomePage/components/Loader";
import Applications from "./Applications";
import DocumentCenter from "./DocumentCenter";
import DSPSection from "./DSPSection";

const TABS = ["Details", "Documents", "Applications"];

function DSPItem({ dspList, setDSPId, smartProfile }) {
	const { userData } = useAuth();
	const [selectedDSPId, setSelectedDSPId] = useState(dspList.length > 0 ? dspList[0].dsp_id : null);
	const { data: dspInfoData, isPending: loading } = useDSPInfo(selectedDSPId);
	const [dspSections, setDspSections] = useState([]);
	const [showDSPModal, setShowDSPModal] = useState(false);
	const [currentTab, setCurrentTab] = useState(TABS[0]);
	const [showDocument, setShowDocument] = useState(false);
	const [documentName, setDocumentName] = useState("");
	const [documentUrl, setDocumentUrl] = useState(null);
	const [loadingDocument, setLoadingDocument] = useState(true);

	useEffect(() => {
		setDSPId(selectedDSPId);
	}, [selectedDSPId]);
	useEffect(() => {
		if (dspInfoData && Object.keys(dspInfoData).length > 0) {
			const _dspSections = [];

			_dspSections.push({
				section: sections.SUMMARY,
				title: "About",
				value: {
					firstName: dspInfoData.firstName,
					lastName: dspInfoData.lastName,
					middleName: dspInfoData.middleName,
					emailAddress: dspInfoData.emailAddress,
					phoneNumber: dspInfoData.phoneNumber,
					linkedInProfile: dspInfoData.linkedInProfile,
					location: dspInfoData.location,
					professionalSummary: dspInfoData.professionalSummary,
					seniorityLevel: dspInfoData.seniorityLevel,
					workedProfessionallySince: dspInfoData.workedProfessionallySince,
					yearsOfExperience: dspInfoData.yearsOfExperience,
					areaOfExpertise: dspInfoData.areaOfExpertise,
				},
			});
			_dspSections.push({
				section: sections.SKILLS,
				title: "Skills",
				value: dspInfoData.skills,
			});
			_dspSections.push({
				section: sections.EXPERIENCE,
				title: "Experience",
				value: dspInfoData.workHistory,
			});
			_dspSections.push({
				section: sections.EDUCATION,
				title: "Education",
				value: dspInfoData.educationHistory,
			});
			_dspSections.push({
				section: sections.CERTIFICATION,
				title: "Certifications",
				value: dspInfoData.certificationsOrLicenses,
			});
			_dspSections.push({
				section: sections.LANGUAGES,
				title: "Languages",
				value: dspInfoData.languagesSpoken,
			});
			_dspSections.push({
				section: sections.REFERENCE,
				title: "References",
				value: dspInfoData.references,
			});
			_dspSections.push({
				section: sections.CUSTOM,
				title: "Volunteer Work",
				value: dspInfoData.volunteerWork,
			});

			setDspSections(_dspSections);
		}
	}, [dspInfoData]);

	return (
		<>
			{showDSPModal && (
				<AnimatePresence>
					<motion.div
						className="fixed inset-0 bg-black/50"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						onClick={() => {
							setShowDSPModal(false);
						}}
						style={{ zIndex: 999 }}
					/>
					<motion.div
						className={cn(
							"fixed top-0 right-0 h-full w-full md:w-3/4 lg:w-2/3 xl:w-1/2 bg-white shadow-lg overflow-y-auto rounded-tl-lg rounded-bl-lg",
							{
								"md:w-full lg:w-[98vw] xl:w-[90vw]": showDocument && documentUrl,
							},
						)}
						initial={{ x: "100%" }}
						animate={{ x: 0 }}
						exit={{ x: "100%" }}
						transition={{
							type: "spring",
							stiffness: 300,
							damping: 30,
						}}
						style={{ zIndex: 1000 }}
					>
						<div className="flex flex-col md:flex-row gap-2">
							{showDocument && documentUrl && (
								<div className="w-full order-2 md:order-1">
									{loadingDocument && (
										<div className="h-[calc(90vh)] flex flex-col items-center justify-center">
											<Loader />
										</div>
									)}
									{!loadingDocument && (
										<div className="flex items-center px-[20px] md:px-8 py-4 gap-2">
											<FileOutlineIcon className="w-[18px] h-[18px]" />
											<h6 className="text-sm font-semibold">{documentName}</h6>
											<Button
												variant="outline"
												className="ml-auto"
												onClick={() => {
													const link = document.createElement("a");
													link.href = documentUrl;
													link.download = documentName || "document";
													link.target = "_blank";
													document.body.appendChild(link);
													link.click();
													document.body.removeChild(link);
												}}
											>
												Download
											</Button>
										</div>
									)}
									<iframe
										src={documentUrl}
										className="w-full h-[calc(90vh)] rounded-lg border border-gray-100 px-[20px] md:px-8"
										title="Document Preview"
										onLoad={() => {
											setLoadingDocument(false);
										}}
									/>
								</div>
							)}
							<div className="w-full py-5 h-full overflow-y-auto order-1 md:order-2">
								<div className="flex px-8 items-start justify-between mb-4">
									<div>
										<h2 className="text-xl font-bold font-plus-jakarta leading-8 mb-2">
											{dspList?.find((item) => item.dsp_id === selectedDSPId)?.dsp_title}
										</h2>
										<Badge className="text-xs font-medium" variant="secondary">
											Created on: April 05, 2025
										</Badge>
									</div>
									<Button
										variant="icon"
										className="text-gray-500"
										onClick={() => {
											setShowDSPModal(false);
										}}
									>
										<X className="h-5 w-5" />
									</Button>
								</div>
								<div className="flex px-8 items-center py-4 border-t border-b mb-6">
									<div className="mr-2 md:mr-4">
										<div className="rounded-full border border-gray-200 p-1">
											<img
												src={userData?.picture || "/images/default-profile.png"}
												alt={`${userData?.firstName || "User"}'s profile picture`}
												className="rounded-full object-cover h-[40px] w-[40px] md:h-[120px] md:w-[120px]"
											/>
										</div>
									</div>
									<div>
										<p className="text-lg font-semibold font-plus-jakarta">
											{capitalizeFirstLetterOfWords(`${dspInfoData?.firstName} ${dspInfoData?.lastName}`)}
										</p>
										<p className="text-gray-600 font-inter font-normal text-sm">
											{dspInfoData?.emailAddress} | {dspInfoData.phoneNumber}
										</p>
										<p className="flex items-center">
											<User2 className="h-3 w-3 text-gray-600 mr-1" />
											<a
												target="_blank"
												href={dspInfoData.linkedInProfile}
												className="text-gray-600 font-inter font-normal text-sm"
												rel="noreferrer"
											>
												{dspInfoData.linkedInProfile}
											</a>
										</p>
										<p className="flex items-center">
											<Globe className="h-3 w-3 text-gray-600 mr-1" />
											<a
												target="_blank"
												href={dspInfoData.personalWebsite}
												className="text-gray-600 font-inter font-normal text-sm"
												rel="noreferrer"
											>
												{dspInfoData.personalWebsite}
											</a>
										</p>
										<p className="text-gray-600 font-inter flex items-center font-normal text-sm">
											<MapPin className="h-3 w-3 text-gray-600 mr-1" />
											{dspInfoData.location}
										</p>
									</div>
								</div>
								<div className="space-y-8 px-8">
									<div className="flex items-center gap-2">
										{TABS.map((item, index) => {
											return (
												<div
													key={index}
													className={cn(
														"text-[#6E7889] text-sm font-medium px-4 py-2 rounded-md cursor-pointer hover:bg-[#EFF8FF] hover:text-[#0B6BCF]",
														{
															"bg-[#EFF8FF] text-[#0B6BCF]": currentTab === item,
														},
													)}
													onClick={() => {
														if (item !== TABS[1]) {
															setShowDocument(false);
															setDocumentName("");
															setDocumentUrl(null);
														}
														setCurrentTab(item);
													}}
												>
													{item}
												</div>
											);
										})}
									</div>
									{loading && <Loader />}
									{!loading && (
										<>
											{currentTab === TABS[0] &&
												dspSections.map((dspSection, index) => {
													return <DSPSection key={index} index={index} data={dspSection} />;
												})}
											{currentTab === TABS[1] && (
												<>
													{smartProfile && Object.keys(smartProfile).length > 0 && (
														<DocumentCenter
															smartProfile={smartProfile}
															selectedDSPId={selectedDSPId}
															setShowDocument={setShowDocument}
															setDocumentName={setDocumentName}
															setDocumentUrl={setDocumentUrl}
															setLoadingDocument={setLoadingDocument}
															profileView={false}
														/>
													)}
												</>
											)}
											{currentTab === TABS[2] && <Applications />}
										</>
									)}
								</div>
							</div>
						</div>
					</motion.div>
				</AnimatePresence>
			)}
			<div className="space-y-[12px] w-full">
				<div className="flex items-center gap-[2px]">
					<div className="text-[20px] font-bold">DSPs</div>
					<DetailedTooltip
						position="right"
						content={
							<div className="px-[6px] py-[7px] w-fit">
								<p className="text-[12px] font-normal w-[244px] text-wrap">
									Dproz Smart Profile is your all-in-one professional profile to help you apply for jobs seamlessly with
									One-Click-Apply.
								</p>
							</div>
						}
					>
						<InfoCircle className="w-[14px]" />
					</DetailedTooltip>
				</div>
				<div className="space-y-4">
					{dspList.map((dsp) => {
						return (
							<div
								key={dsp.dsp_id}
								className="flex items-center gap-4 px-5 py-2 border border-gray-200 rounded-[6px] cursor-pointer"
								onClick={() => {
									setSelectedDSPId(dsp.dsp_id);
									setShowDSPModal(true);
								}}
							>
								<Icon iconName="invoice" size={24} />
								<div className="p-[4px]">
									<h6 className="text-base font-inter font-bold">{dsp.dsp_title}</h6>
									<p className="text-gray-600 text-sm font-normal">Last updated Mar 18, 2024</p>
								</div>
								<div className="flex items-center gap-2 ml-auto">
									{dspInfoData?.processing_status === "pending" && (
										<Badge className="bg-[#FFECC7] text-[#985508] border-none">Processing</Badge>
									)}
									<ChevronRight color="#6A7383" size={16} />
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</>
	);
}

export default DSPItem;
